import React, { useState } from 'react';
import { categories, mainSiteUrl } from '@/mocks/categories';

const col1 = categories.slice(0, 5);
const col2 = categories.slice(5, 10);
const col3 = categories.slice(10, 14);

export default function Footer() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('Dziękujemy za zapisanie się do newslettera!');
      return;
    }

    const formData = new FormData(form);
    const urlEncoded = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'website_alt') {
        urlEncoded.append(key, value as string);
      }
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d975gakut53jbls7kv30', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });
      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { parsed = {}; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('Dziękujemy za zapisanie się do newslettera!');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '';
        setFormStatus('error');
        setFormMessage(serverMsg || 'Wystąpił błąd. Spróbuj ponownie później.');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('Wystąpił błąd połączenia. Spróbuj ponownie.');
    }
  };

  return (
    <footer className="w-full bg-background-100 border-t border-background-200/70">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-14 md:py-20">
        <div className="flex flex-col lg:flex-row gap-12">
          <div className="w-full lg:w-2/5">
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 leading-tight">
              Kakobuy<br />Finds
            </h3>
            <p className="text-sm text-foreground-500 mt-4 max-w-sm leading-relaxed">
              Największa baza produktów i finds w języku polskim. Odkrywaj sprawdzone kategorie, oszczędzaj czas i znajdź to, czego naprawdę szukasz.
            </p>

            <div className="mt-8">
              <p className="text-xs font-semibold text-foreground-600 uppercase tracking-wider mb-3">Subskrybuj newsletter</p>
              <form
                data-readdy-form="newsletter-footer"
                id="newsletter-footer"
                action="https://readdy.ai/api/form/d975gakut53jbls7kv30"
                method="POST"
                className="flex flex-col sm:flex-row gap-3"
                onSubmit={handleSubmit}
              >
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Twój adres email"
                  className="flex-1 px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                  required
                />
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-lg bg-foreground-950 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Zapisz się
                </button>
              </form>
              {formStatus !== 'idle' && (
                <p className={`mt-3 text-xs ${formStatus === 'success' ? 'text-primary-600' : 'text-red-500'}`}>
                  {formMessage}
                </p>
              )}
            </div>

            <div className="mt-8">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-foreground-300 text-foreground-700 text-sm font-semibold hover:border-foreground-700 hover:text-foreground-950 transition-all whitespace-nowrap cursor-pointer"
              >
                buy it now
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>

          <div className="w-full lg:w-3/5">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-8">
              <div>
                <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-4">Kategorie 1</h4>
                <ul className="space-y-2.5">
                  {col1.map((cat) => (
                    <li key={cat.name}>
                      <a
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="text-sm text-foreground-600 hover:text-foreground-950 hover:underline transition-colors"
                      >
                        {cat.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-4">Kategorie 2</h4>
                <ul className="space-y-2.5">
                  {col2.map((cat) => (
                    <li key={cat.name}>
                      <a
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="text-sm text-foreground-600 hover:text-foreground-950 hover:underline transition-colors"
                      >
                        {cat.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-4">Kategorie 3</h4>
                <ul className="space-y-2.5">
                  {col3.map((cat) => (
                    <li key={cat.name}>
                      <a
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="text-sm text-foreground-600 hover:text-foreground-950 hover:underline transition-colors"
                      >
                        {cat.name}
                      </a>
                    </li>
                  ))}
                  <li>
                    <a
                      href="/artykul/kakobuy-sheet-kompletny-przewodnik"
                      className="text-sm text-primary-600 hover:text-primary-700 hover:underline transition-colors font-medium"
                    >
                      Poradnik Kakobuy
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-background-200/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-400">
            &copy; {new Date().getFullYear()} Kakobuy Finds. Wszelkie prawa zastrzeżone. Strona ma charakter wyłącznie informacyjny.
          </p>
          <a
            href="/"
            className="text-xs text-foreground-400 hover:text-foreground-700 transition-colors"
          >
            Strona główna
          </a>
        </div>
      </div>
    </footer>
  );
}